import { apiClientV2 } from "../common";
import type {
  ProductPurchaseDefaults,
  PurchaseDefaultsResult,
} from "../types";

/** Igual que MAX_CODES en el backend; se trocea antes de llegar al 400. */
const MAX_CODES_PER_CALL = 200;

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

function normalizeEntry(raw: any): ProductPurchaseDefaults {
  return {
    product_code: String(raw?.productCode ?? raw?.product_code ?? ""),
    description: String(raw?.description ?? ""),
    vendor_name: String(raw?.vendorName ?? raw?.vendor_name ?? ""),
    unit_cost:
      raw?.unitCost ?? raw?.unit_cost ?? null,
    unit_cost_source: String(
      raw?.unitCostSource ?? raw?.unit_cost_source ?? "none",
    ),
    uom: String(raw?.uom ?? ""),
    vendor_code: String(raw?.vendorCode ?? raw?.vendor_code ?? ""),
    vendor_code_source: String(
      raw?.vendorCodeSource ?? raw?.vendor_code_source ?? "none",
    ),
    warehouse_code: String(raw?.warehouseCode ?? raw?.warehouse_code ?? ""),
    warehouse_code_source: String(
      raw?.warehouseCodeSource ?? raw?.warehouse_code_source ?? "none",
    ),
    vendors: (raw?.vendors ?? []).map((v: any) => ({
      vendor_code: String(v?.vendorCode ?? v?.vendor_code ?? ""),
      erp_code: String(
        v?.erpCode ?? v?.erp_code ?? v?.vendorCode ?? v?.vendor_code ?? "",
      ),
      unit_price: v?.unitPrice ?? v?.unit_price ?? null,
      uom: String(v?.uom ?? ""),
      discount_pctg: v?.discountPctg ?? v?.discount_pctg ?? null,
      min_order_quantity: v?.minOrderQuantity ?? v?.min_order_quantity ?? null,
      avg_production_days:
        v?.avgProductionDays ?? v?.avg_production_days ?? null,
      plant_code: String(v?.plantCode ?? v?.plant_code ?? ""),
    })),
    warehouses: (raw?.warehouses ?? []).map((w: any) => ({
      warehouse_code: String(w?.warehouseCode ?? w?.warehouse_code ?? ""),
      erp_code: String(
        w?.erpCode ??
          w?.erp_code ??
          w?.referenceWarehouseCode ??
          w?.warehouseCode ??
          w?.warehouse_code ??
          "",
      ),
      name: String(w?.name ?? ""),
      reference_code: String(w?.referenceCode ?? w?.reference_code ?? ""),
      actual_quantity: w?.actualQuantity ?? w?.actual_quantity ?? null,
    })),
  };
}

/**
 * GET /api/v2/coverage_simulator/products/purchase-defaults/?codes=A,B,C
 *
 * El backend responde 200 con `datamartAvailable: false` cuando el datamart no
 * contesta, en vez de un 5xx: el editor tiene que poder abrir igual, con el
 * costo local y sin selector de almacén.
 */
export async function apiGetPurchaseDefaults(
  productCodes: string[],
): Promise<PurchaseDefaultsResult> {
  const codes = Array.from(
    new Set(productCodes.map((c) => String(c ?? "").trim()).filter(Boolean)),
  );
  const empty: PurchaseDefaultsResult = {
    byCode: {},
    vendorCatalog: [],
    datamartAvailable: true,
    datamartError: "",
  };
  if (codes.length === 0) {
    return empty;
  }

  const result: PurchaseDefaultsResult = { ...empty, byCode: {} };
  for (const group of chunk(codes, MAX_CODES_PER_CALL)) {
    const res = await apiClientV2.get(
      "coverage_simulator/products/purchase-defaults/",
      { params: { codes: group.join(",") } },
    );
    const body = res.data ?? {};
    const rows: any[] = body.data ?? [];
    rows.map(normalizeEntry).forEach((entry) => {
      if (entry.product_code) {
        result.byCode[entry.product_code] = entry;
      }
    });
    const metadata = body.metadata ?? {};
    const catalog = metadata.vendorCatalog ?? metadata.vendor_catalog ?? [];
    if (Array.isArray(catalog) && catalog.length > 0) {
      result.vendorCatalog = catalog.map((v: any) => ({
        vendor_code: String(v?.vendorCode ?? v?.vendor_code ?? ""),
        erp_code: String(
          v?.erpCode ??
            v?.erp_code ??
            v?.referenceCode ??
            v?.vendorCode ??
            v?.vendor_code ??
            "",
        ),
        name: String(v?.name ?? ""),
        reference_code: String(v?.referenceCode ?? v?.reference_code ?? ""),
      }));
    }
    // Basta que una tanda falle para que los datos estén incompletos: la UI
    // debe avisar, no promediar.
    const available =
      metadata.datamartAvailable ?? metadata.datamart_available ?? true;
    if (!available) {
      result.datamartAvailable = false;
      result.datamartError = String(
        metadata.datamartError ?? metadata.datamart_error ?? "",
      );
    }
  }
  return result;
}
